import {
  cacheKeys,
  FIND_ROUTINE_ASSIGNMENTS,
  useCurrentWorkspace,
  useCustomMutation,
  useCustomQuery,
  WorkspaceType,
} from '@gripp/shared-logic';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  AssignmentModal,
  AssignmentTab,
} from '../../shared/components/assignment/assignmentModal';
import { EditRoutineAssetsList } from './editRoutineAssetsList';
import { RoutineAddonsAssignList } from './routineAddonsAssignList';

const ASSIGN_TO_ROUTINE = `
  mutation AssignToRoutine($input: AssignToRoutineInput!) {
    routine {
      assignToRoutine(input: $input) {
        message
        success
      }
    }
  }
`;

type Props = {
  routineId: string;
  title: string;
  showCheckboxColumn: boolean;
  onComplete: () => void;
  /** 'assets' | 'addons' – which tab to show when modal opens (default: assets) */
  initialTab?: 'assets' | 'addons';
};

export const RoutineAssetsForm = ({
  routineId,
  title,
  onComplete,
  initialTab,
}: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const currentWorkspace = useCurrentWorkspace();
  const isOEM = currentWorkspace?.type === WorkspaceType.Oem;

  const { data, isPending } = useCustomQuery({
    query: FIND_ROUTINE_ASSIGNMENTS,
    cacheKey: ['routine', 'assignments', routineId],
    variables: { routineId },
  });
  const found = data?.routine?.findRoutineAssignments;

  const { mutateAsync: assignToRoutine } = useCustomMutation({
    query: ASSIGN_TO_ROUTINE,
  });

  const tabs: AssignmentTab[] = [
    {
      key: 'assets',
      label: t('groups.assignments.assets'),
      searchPlaceholder: t('groups.assignments.assetSearchPlaceholder'),
      assignments: found?.assets,
      onSave: async ({ selected }) => {
        await assignToRoutine(
          {
            input: { assetIds: selected, routineId, workspaceId: currentWorkspace?.id },
          } as never,
          {
            onSettled: async () => {
              await queryClient.invalidateQueries({
                queryKey: cacheKeys('routine').all,
              });
            },
          }
        );
      },
      renderList: (p) => (
        <EditRoutineAssetsList
          workspaceId={currentWorkspace!.id}
          routineId={routineId}
          maxHeight={p.maxHeight}
          keyword={p.keyword}
          assignments={p.assignments}
          updateAssignments={p.onAssignmentsChange}
        />
      ),
    },
  ];

  if (isOEM) {
    tabs.push({
      key: 'addons',
      label: t('product.assignments.addons'),
      searchPlaceholder: t('groups.assignments.addonsSearchPlaceholder'),
      assignments: found?.addons ?? [],
      onSave: async ({ selected }) => {
        await assignToRoutine(
          {
            input: { addonIds: selected, routineId, workspaceId: currentWorkspace?.id },
          } as never,
          {
            onSettled: async () => {
              await queryClient.invalidateQueries({
                queryKey: cacheKeys('routine').all,
                exact: false,
              });
              await queryClient.invalidateQueries({
                queryKey: ['addon'],
                exact: false,
              });
            },
          }
        );
      },
      renderList: (p) => (
        <RoutineAddonsAssignList
          workspaceId={currentWorkspace!.id}
          routineId={routineId}
          maxHeight={p.maxHeight}
          keyword={p.keyword}
          assignments={p.assignments}
          updateAssignments={p.onAssignmentsChange}
        />
      ),
    });
  }

  return (
    <AssignmentModal
      title={title}
      isLoading={isPending || !currentWorkspace?.id}
      initialTabKey={initialTab}
      onComplete={onComplete}
      tabs={tabs}
    />
  );
};
